import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { topbar } from "../components/common.js";
import { spinner, errorState, emptyState, toast } from "../components/ui.js";
import { pendingRequests } from "../store.js";
import { relativeDay, fmtDuration, fmtVolume, parseDate } from "../lib/format.js";

export function avatar(user, size = 44) {
  if (user.gravatar_url) return h("img", { src: user.gravatar_url, alt: "", style: { width: size + "px", height: size + "px", borderRadius: "50%", flexShrink: 0 } });
  const letter = (user.display_name || user.username || "?").charAt(0).toUpperCase();
  return h("div", { class: "avatar-fallback", style: { width: size + "px", height: size + "px", borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--surface-2, var(--border))", fontWeight: 700, flexShrink: 0 } }, letter);
}

function mutualBadge() {
  return h("span", { class: "badge", style: { background: "var(--accent)", color: "#fff" } }, "Friends");
}

export default function Friends() {
  const root = h("div", { class: "screen" });
  let tab = "following";
  let pendingCount = 0;

  loadCounts().then(render);

  async function loadCounts() {
    try { pendingCount = (await api.friendRequests()).incoming.length; } catch { pendingCount = 0; }
    pendingRequests(pendingCount); // keep the nav badge in sync
  }

  function render() {
    const findBtn = h("button", {
      class: "icon-btn",
      "aria-label": "Find people",
      style: tab === "find" ? { background: "var(--accent-soft)", color: "var(--accent)" } : null,
      onClick: () => { tab = "find"; render(); },
    }, icon("search", { size: 20 }));
    setChildren(root,
      topbar("Friends", { actions: findBtn }),
      h("div", { class: "tabs" },
        tabBtn("following", "Following"),
        tabBtn("followers", "Followers"),
        tabBtn("activity", "Activity"),
        tabBtn("requests", pendingCount ? `Requests (${pendingCount})` : "Requests")
      ),
      h("div", { class: "tab-body mt" })
    );
    renderTab();
  }

  function tabBtn(key, label) {
    return h("button", { class: tab === key && "active", onClick: () => { tab = key; render(); } }, label);
  }

  function body() { return root.querySelector(".tab-body"); }

  function renderTab() {
    if (tab === "following") return followingTab();
    if (tab === "followers") return followersTab();
    if (tab === "activity") return activityTab();
    if (tab === "requests") return requestsTab();
    return findTab();
  }

  // ── Following (people I follow, with presence) ───────────────────────────────
  async function followingTab() {
    setChildren(body(), spinner());
    try {
      const list = await api.friends();
      setChildren(body(),
        list.length
          ? h("div", null, ...list.map(followingRow))
          : emptyState({ icon: "user", title: "Not following anyone yet", message: "Find people on the Find tab and follow them." })
      );
    } catch (e) {
      setChildren(body(), errorState(e.message, followingTab));
    }
  }

  function followingRow(f) {
    let status;
    if (!f.shares_activity) status = h("span", { class: "dim" }, "Activity hidden");
    else if (f.working_out) status = h("span", { class: "hstack", style: { gap: "6px", color: "var(--success)" } }, h("span", { class: "pulse" }), "Training now");
    else status = h("span", { class: "dim" }, f.last_workout_at ? `Last trained ${relativeDay(f.last_workout_at).toLowerCase()}` : "No workouts yet");
    return h("div", { class: "card card-tap row-between", style: { marginBottom: "10px", alignItems: "center" }, onClick: () => navigate(`/friends/${f.id}`) },
      h("div", { class: "hstack", style: { gap: "12px", alignItems: "center" } },
        avatar(f),
        h("div", null,
          h("div", { class: "hstack", style: { gap: "8px", alignItems: "center" } }, h("span", { style: { fontWeight: 700 } }, f.display_name || f.username), f.is_mutual ? mutualBadge() : null),
          h("div", { style: { fontSize: ".84rem", marginTop: "2px" } }, status)
        )
      ),
      f.shares_activity ? h("span", { class: "badge" }, `${f.week_sessions} this wk`) : icon("forward", { size: 18, className: "icon muted" })
    );
  }

  // ── Followers (people who follow me) ─────────────────────────────────────────
  async function followersTab() {
    setChildren(body(), spinner());
    try {
      const list = await api.followers();
      setChildren(body(),
        list.length
          ? h("div", null, ...list.map(followerRow))
          : emptyState({ icon: "users", title: "No followers yet", message: "Share your username so people can follow you." })
      );
    } catch (e) {
      setChildren(body(), errorState(e.message, followersTab));
    }
  }

  function followerRow(u) {
    let action;
    if (u.is_mutual) action = mutualBadge();
    else if (u.i_follow === "pending") action = h("span", { class: "dim" }, "Requested");
    else action = h("button", { class: "btn btn-ghost", onClick: async () => { try { const r = await api.follow(u.id); toast(r.status === "accepted" ? "Following back" : "Request sent", "success"); followersTab(); } catch (e) { toast(e.message, "error"); } } }, icon("plus", { size: 16 }), "Follow back");
    return h("div", { class: "card row-between", style: { marginBottom: "10px", alignItems: "center" } },
      h("div", { class: "hstack card-tap", style: { gap: "12px", alignItems: "center" }, onClick: () => navigate(`/friends/${u.id}`) },
        avatar(u, 40),
        h("div", null, h("div", { style: { fontWeight: 700 } }, u.display_name || u.username), h("div", { class: "dim", style: { fontSize: ".82rem" } }, `@${u.username}`))
      ),
      action
    );
  }

  // ── Activity feed ─────────────────────────────────────────────────────────────
  async function activityTab() {
    setChildren(body(), spinner());
    try {
      const feed = await api.friendActivity();
      const items = [];
      if (feed.training_now.length) {
        items.push(h("div", { class: "section-title", style: { marginTop: "2px" } }, "Training now"));
        items.push(...feed.training_now.map(nowRow));
      }
      if (feed.recent.length) {
        items.push(h("div", { class: "section-title" }, "Recent workouts"));
        items.push(...feed.recent.map(recentRow));
      }
      setChildren(body(), items.length ? h("div", null, ...items) : emptyState({ icon: "history", title: "No activity yet", message: "When people you follow train, it shows up here." }));
    } catch (e) {
      setChildren(body(), errorState(e.message, activityTab));
    }
  }

  function nowRow(a) {
    const start = parseDate(a.started_at)?.getTime() ?? Date.now();
    return h("div", { class: "card card-tap row-between", style: { marginBottom: "10px", alignItems: "center" }, onClick: () => navigate(`/friends/${a.user.id}`) },
      h("div", { class: "hstack", style: { gap: "12px", alignItems: "center" } },
        avatar(a.user, 40),
        h("div", null,
          h("div", { style: { fontWeight: 700 } }, a.user.display_name || a.user.username),
          h("div", { class: "hstack", style: { gap: "6px", color: "var(--success)", fontSize: ".84rem", marginTop: "2px" } }, h("span", { class: "pulse" }), a.name || "Working out")
        )
      ),
      h("span", { class: "dim", style: { fontSize: ".8rem" } }, fmtDuration(Math.floor((Date.now() - start) / 1000)))
    );
  }

  function recentRow(a) {
    const kudosBtn = h("button", {
      class: ["btn", "btn-ghost", a.i_gave && "kudos-on"],
      "aria-pressed":String(!!a.i_gave),
      "aria-label":`${a.i_gave?"Remove":"Give"} kudos for ${a.user.display_name || a.user.username}’s ${a.name || "workout"}`,
      style: { padding: "6px 10px", color: a.i_gave ? "var(--accent, #f5a623)" : null },
      onClick: async (e) => {
        e.stopPropagation();
        try {
          const res = a.i_gave ? await api.removeKudos(a.session_id) : await api.giveKudos(a.session_id);
          a.i_gave = res.i_gave; a.kudos_count = res.kudos_count;
          kudosBtn.classList.toggle("kudos-on",!!a.i_gave);
          kudosBtn.setAttribute("aria-pressed",String(!!a.i_gave));
          kudosBtn.setAttribute("aria-label",`${a.i_gave?"Remove":"Give"} kudos for ${a.user.display_name || a.user.username}’s ${a.name || "workout"}`);
          kudosBtn.textContent=`👏 ${a.i_gave?"Supported":"Kudos"} · ${a.kudos_count}`;
          toast(a.i_gave?"Encouragement sent":"Kudos removed");
        } catch (err) { toast(err.message, "error"); }
      },
    }, `👏 ${a.i_gave?"Supported":"Kudos"} · ${a.kudos_count}`);
    return h("div", { class: "card", style: { marginBottom: "10px" } },
      h("div", { class: "row-between", style: { alignItems: "center" } },
        h("div", { class: "hstack card-tap", style: { gap: "12px", alignItems: "center" }, onClick: () => navigate(`/friends/${a.user.id}`) },
          avatar(a.user, 40),
          h("div", null,
            h("div", { style: { fontWeight: 700 } }, a.user.display_name || a.user.username),
            h("div", { class: "dim", style: { fontSize: ".82rem", marginTop: "2px" } }, `${a.name || "Workout"} · ${relativeDay(a.ended_at || a.started_at).toLowerCase()}`)
          )
        ),
        kudosBtn
      ),
      h("div", { class: "hstack", style: { gap: "14px", marginTop: "8px", fontSize: ".8rem" } },
        h("span", { class: "dim" }, `${a.set_count} sets`),
        a.duration_seconds ? h("span", { class: "dim" }, fmtDuration(a.duration_seconds)) : null,
        a.volume_kg ? h("span", { class: "dim" }, fmtVolume(a.volume_kg)) : null
      )
    );
  }

  // ── Requests (incoming follow approvals + my pending) ────────────────────────
  async function requestsTab() {
    setChildren(body(), spinner());
    try {
      const { incoming, outgoing } = await api.friendRequests();
      pendingCount = incoming.length;
      pendingRequests(pendingCount);
      const sections = [];
      sections.push(h("div", { class: "section-title", style: { marginTop: "2px" } }, "Wants to follow you"));
      sections.push(incoming.length ? h("div", null, ...incoming.map(incomingRow)) : h("p", { class: "dim" }, "No pending requests."));
      sections.push(h("div", { class: "section-title" }, "Your requests"));
      sections.push(outgoing.length ? h("div", null, ...outgoing.map(outgoingRow)) : h("p", { class: "dim" }, "No pending requests."));
      setChildren(body(), h("div", null, ...sections));
      root.querySelector(".tabs button:nth-child(4)").textContent = pendingCount ? `Requests (${pendingCount})` : "Requests";
    } catch (e) {
      setChildren(body(), errorState(e.message, requestsTab));
    }
  }

  function incomingRow(r) {
    return h("div", { class: "card row-between", style: { marginBottom: "10px", alignItems: "center" } },
      userCell(r.user),
      h("div", { class: "btn-row" },
        h("button", { class: "btn btn-primary", onClick: async () => { try { await api.acceptFollow(r.user.id); toast("Follower approved", "success"); requestsTab(); } catch (e) { toast(e.message, "error"); } } }, icon("check", { size: 16 }), "Approve"),
        h("button", { class: "icon-btn", "aria-label": "Decline", onClick: async () => { try { await api.removeFollower(r.user.id); requestsTab(); } catch (e) { toast(e.message, "error"); } } }, icon("x", { size: 18 }))
      )
    );
  }

  function outgoingRow(r) {
    return h("div", { class: "card row-between", style: { marginBottom: "10px", alignItems: "center" } },
      userCell(r.user),
      h("button", { class: "btn btn-ghost", onClick: async () => { try { await api.unfollow(r.user.id); requestsTab(); } catch (e) { toast(e.message, "error"); } } }, "Cancel")
    );
  }

  // ── Find people ─────────────────────────────────────────────────────────────────
  function findTab() {
    let timer = null;
    const results = h("div", { class: "mt" });
    const search = h("input", { class: "input", type: "search", placeholder: "Search by username…", oninput: (e) => { clearTimeout(timer); const q = e.target.value; timer = setTimeout(() => doSearch(q, results), 300); } });
    setChildren(body(), h("div", { class: "field" }, search), results);
    setTimeout(() => search.focus(), 0);
  }

  async function doSearch(q, results) {
    if (q.trim().length < 2) { setChildren(results, h("p", { class: "dim" }, "Type at least 2 characters.")); return; }
    setChildren(results, spinner());
    try {
      const users = await api.searchUsers(q);
      setChildren(results, users.length ? h("div", null, ...users.map((u) => searchRow(u, results))) : emptyState({ icon: "search", title: "No users found" }));
    } catch (e) {
      setChildren(results, errorState(e.message));
    }
  }

  function searchRow(u, results) {
    let action;
    if (u.following === "accepted") action = h("span", { class: "badge" }, "Following");
    else if (u.following === "pending") action = h("span", { class: "dim" }, "Requested");
    else action = h("button", { class: "btn btn-primary", onClick: () => follow(u, results) }, icon("plus", { size: 16 }), u.is_private ? "Request" : "Follow");
    const sub = h("div", { class: "dim", style: { fontSize: ".82rem" } }, `@${u.username}`, u.follows_me ? " · follows you" : "");
    return h("div", { class: "card row-between", style: { marginBottom: "10px", alignItems: "center" } },
      h("div", { class: "hstack", style: { gap: "12px", alignItems: "center" } }, avatar(u, 40), h("div", null, h("div", { style: { fontWeight: 700 } }, u.display_name || u.username), sub)),
      action
    );
  }

  async function follow(u, results) {
    try {
      const r = await api.follow(u.id);
      toast(r.status === "accepted" ? `Following ${u.display_name || u.username}` : "Request sent", "success");
      await loadCounts();
      // refresh the current search results to reflect the new state
      const q = root.querySelector('input[type="search"]')?.value || "";
      doSearch(q, results);
    } catch (e) {
      toast(e.message, "error");
    }
  }

  function userCell(u) {
    return h("div", { class: "hstack", style: { gap: "12px", alignItems: "center" } },
      avatar(u, 40),
      h("div", null,
        h("div", { style: { fontWeight: 700 } }, u.display_name || u.username),
        h("div", { class: "dim", style: { fontSize: ".82rem" } }, `@${u.username}`)
      )
    );
  }

  return root;
}
